import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FileItem } from '../services/storageService';

// ── Types ──────────────────────────────────────────────────────────────────────

export type VfxEffectType =
  | 'blur' | 'pixelate' | 'grayscale' | 'sepia' | 'negative'
  | 'tint' | 'brightness' | 'contrast' | 'saturation'
  | 'vignette' | 'film_grain' | 'edge_detect'
  | 'mirror_h' | 'mirror_v' | 'chromatic_aberration';

interface ParamDef {
  key: string;
  label: string;
  type: 'range' | 'color';
  min?: number; max?: number; step?: number;
  default: number | string;
}

export interface VfxEffect {
  id: string;
  type: VfxEffectType;
  params: Record<string, number | string>;
}

// ── Segmentation IA types (inline to avoid circular dep) ──────────────────────

export type SegEffect =
  | 'background-blur' | 'background-replace' | 'background-pixelate'
  | 'body-parts' | 'silhouette';

export type SegPartGroup = 'face' | 'hair' | 'body' | 'clothes' | 'accessories';
export type SegPartEffect = 'blur' | 'color' | 'pixelate' | 'hide';

export interface SegPartGroupConfig {
  enabled: boolean;
  effect: SegPartEffect;
  color: string;
  intensity: number;
}

const SEG_EFFECT_LABELS: Record<SegEffect, string> = {
  'background-blur':     '🌫️ Flou arrière-plan',
  'background-replace':  '🖼️ Remplacement fond',
  'background-pixelate': '⬜ Pixelisation fond',
  'body-parts':          '🎭 Effets par zones corporelles',
  'silhouette':          '👤 Silhouette',
};

const SEG_PART_LABELS: Record<SegPartGroup, string> = {
  face: '😶 Visage (peau)', hair: '💇 Cheveux', body: '🧍 Corps (peau)', clothes: '👕 Vêtements', accessories: '💍 Accessoires',
};

// SelfieMulticlass 256×256 category IDs: 0=background, 1=hair, 2=body-skin, 3=face-skin, 4=clothes, 5=accessories
const MULTICLASS_IDS: Record<SegPartGroup, number> = {
  face: 3, hair: 1, body: 2, clothes: 4, accessories: 5,
};

const DEFAULT_SEG_PARTS: Record<SegPartGroup, SegPartGroupConfig> = {
  face:        { enabled: false, effect: 'blur',  color: '#ff6600', intensity: 60 },
  hair:        { enabled: false, effect: 'color', color: '#8844ff', intensity: 70 },
  body:        { enabled: false, effect: 'color', color: '#0066ff', intensity: 50 },
  clothes:     { enabled: false, effect: 'color', color: '#00cc44', intensity: 50 },
  accessories: { enabled: false, effect: 'color', color: '#ffcc00', intensity: 50 },
};

// ── Constants ──────────────────────────────────────────────────────────────────

const ZONE_COLORS = [
  'rgba(99,102,241,0.5)', 'rgba(236,72,153,0.5)', 'rgba(245,158,11,0.5)',
  'rgba(16,185,129,0.5)', 'rgba(239,68,68,0.5)',  'rgba(6,182,212,0.5)',
];

// ── Utilities ──────────────────────────────────────────────────────────────────

let _eid = 0;
const uid = () => `vfx${++_eid}_${Math.random().toString(36).slice(2, 6)}`;

const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const ss = (s % 60).toFixed(1).padStart(4, '0');
  return `${m}:${ss}`;
};

function defaultParams(type: VfxEffectType): Record<string, number | string> {
  return Object.fromEntries(VFX_DEFS[type].params.map(d => [d.key, d.default]));
}

// ── SegConfig interface & default ─────────────────────────────────────────────

export interface SegConfig {
  id: string;
  segEffect: SegEffect;
  segBlurRadius: number;
  segBgColor: string;
  segBgMediaUrl?: string;
  segBgMediaType?: 'image' | 'video';
  segPixelSize: number;
  segSilhouetteColor: string;
  segSilhouetteOpacity: number;
  segSilhouetteOnVideo: boolean;
  segPartGroups: Record<SegPartGroup, SegPartGroupConfig>;
}

const DEFAULT_SEG_ITEM = (): SegConfig => ({
  id: uid(),
  segEffect: 'background-blur',
  segBlurRadius: 15,
  segBgColor: '#000000',
  segPixelSize: 12,
  segSilhouetteColor: '#ff3366',
  segSilhouetteOpacity: 0.85,
  segSilhouetteOnVideo: true,
  segPartGroups: { ...DEFAULT_SEG_PARTS },
});

export interface VfxZone {
  id: string;
  start: number;
  end: number;
  color: string;
  label: string;
  effects: VfxEffect[];
  // AI Segmentation (optional per zone)
  segItems?: SegConfig[];
}

// ── Effect definitions ─────────────────────────────────────────────────────────

const VFX_DEFS: Record<VfxEffectType, { label: string; emoji: string; params: ParamDef[] }> = {
  blur:                 { label: 'Flou', emoji: '🌫', params: [{ key: 'radius', label: 'Rayon', type: 'range', min: 1, max: 30, step: 1, default: 8 }] },
  pixelate:             { label: 'Pixelisation', emoji: '⬛', params: [{ key: 'size', label: 'Taille px', type: 'range', min: 2, max: 60, step: 1, default: 16 }] },
  grayscale:            { label: 'Noir & Blanc', emoji: '◻', params: [] },
  sepia:                { label: 'Sépia', emoji: '🟫', params: [] },
  negative:             { label: 'Négatif', emoji: '🔄', params: [] },
  tint:                 { label: 'Teinte couleur', emoji: '🎨', params: [
    { key: 'color',   label: 'Couleur',  type: 'color', default: '#ff0000' },
    { key: 'opacity', label: 'Opacité',  type: 'range', min: 0.05, max: 0.9, step: 0.05, default: 0.4 },
  ]},
  brightness:           { label: 'Luminosité', emoji: '☀', params: [{ key: 'value', label: 'Valeur', type: 'range', min: -0.8, max: 0.8, step: 0.05, default: 0.2 }] },
  contrast:             { label: 'Contraste',   emoji: '◑', params: [{ key: 'value', label: 'Valeur', type: 'range', min: 0.2,  max: 3.0, step: 0.1,  default: 1.5 }] },
  saturation:           { label: 'Saturation',  emoji: '🌈', params: [{ key: 'value', label: 'Valeur', type: 'range', min: 0,    max: 3.0, step: 0.1,  default: 1.5 }] },
  vignette:             { label: 'Vignette',    emoji: '🔲', params: [{ key: 'intensity', label: 'Intensité', type: 'range', min: 0.1, max: 1.5, step: 0.05, default: 0.8 }] },
  film_grain:           { label: 'Grain cinéma', emoji: '📽', params: [{ key: 'intensity', label: 'Intensité', type: 'range', min: 0.05, max: 1.0, step: 0.05, default: 0.4 }] },
  edge_detect:          { label: 'Contours (Sobel)', emoji: '✏', params: [] },
  mirror_h:             { label: 'Miroir horizontal', emoji: '↔', params: [] },
  mirror_v:             { label: 'Miroir vertical',   emoji: '↕', params: [] },
  chromatic_aberration: { label: 'Aberration chromatique', emoji: '🌐', params: [{ key: 'shift', label: 'Décalage px', type: 'range', min: 2, max: 40, step: 1, default: 10 }] },
};

/** Approximate CSS filter string for live preview */
function toCssFilter(effects: VfxEffect[]): string {
  const parts: string[] = [];
  for (const e of effects) {
    const p = e.params;
    switch (e.type) {
      case 'blur':        parts.push(`blur(${Math.max(1, Math.round(Number(p.radius) / 3))}px)`); break;
      case 'grayscale':   parts.push('grayscale(1)'); break;
      case 'sepia':       parts.push('sepia(1)'); break;
      case 'negative':    parts.push('invert(1)'); break;
      case 'brightness':  parts.push(`brightness(${(1 + Number(p.value)).toFixed(2)})`); break;
      case 'contrast':    parts.push(`contrast(${Number(p.value).toFixed(2)})`); break;
      case 'saturation':  parts.push(`saturate(${Number(p.value).toFixed(2)})`); break;
      case 'edge_detect': parts.push('grayscale(1) contrast(8)'); break;
      case 'pixelate': {
        // CSS can't truly pixelate — show a very light blur as indicator
        const sz = Number(p.size) || 16;
        parts.push(`blur(${Math.max(0.5, sz / 20).toFixed(1)}px)`);
        break;
      }
      case 'chromatic_aberration': {
        const sh = Math.round((Number(p.shift) || 10) / 2);
        parts.push(`drop-shadow(${sh}px 0 0 rgba(255,0,0,0.6)) drop-shadow(-${sh}px 0 0 rgba(0,100,255,0.6))`);
        break;
      }
      case 'mirror_h':    /* handled via transform */ break;
      case 'mirror_v':    /* handled via transform */ break;
      // tint / vignette / film_grain rendered via overlay divs
      default: break;
    }
  }
  return parts.join(' ') || 'none';
}

function toCssTransform(effects: VfxEffect[]): string {
  const parts: string[] = [];
  if (effects.some(e => e.type === 'mirror_h')) parts.push('scaleX(-1)');
  if (effects.some(e => e.type === 'mirror_v')) parts.push('scaleY(-1)');
  return parts.join(' ') || 'none';
}

/** Overlay divs for effects that can't be expressed as CSS filters */
function getEffectOverlays(effects: VfxEffect[]): React.CSSProperties[] {
  const overlays: React.CSSProperties[] = [];
  for (const e of effects) {
    const p = e.params;
    switch (e.type) {
      case 'tint': {
        const hex = String(p.color || '#ff0000').replace('#', '').padEnd(6, '0');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const op = Math.max(0.05, Math.min(0.9, Number(p.opacity) || 0.4));
        overlays.push({ position: 'absolute', inset: 0, background: `rgba(${r},${g},${b},${op})`, pointerEvents: 'none' });
        break;
      }
      case 'vignette': {
        const intensity = Math.max(0.1, Number(p.intensity) || 0.8);
        const stop = Math.round(Math.max(20, 55 - intensity * 20));
        const alpha = Math.min(0.97, intensity * 0.75).toFixed(2);
        overlays.push({ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, transparent ${stop}%, rgba(0,0,0,${alpha}) 100%)`, pointerEvents: 'none' });
        break;
      }
      case 'film_grain': {
        const intensity = Math.max(0.05, Number(p.intensity) || 0.4);
        const op = Math.min(0.75, intensity * 0.7).toFixed(2);
        const svgNoise = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E")`;
        overlays.push({ position: 'absolute', inset: 0, backgroundImage: svgNoise, backgroundSize: '200px 200px', opacity: Number(op), mixBlendMode: 'overlay', pointerEvents: 'none' });
        break;
      }
      default: break;
    }
  }
  return overlays;
}

// ── Canvas rendering helpers ───────────────────────────────────────────────────

function hexToRgb(hex: string) {
  const n = parseInt(hex.replace('#', ''), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Apply standard VFX effects to a canvas (canvas already contains video frame). */
function applyCanvasVfx(ctx: CanvasRenderingContext2D, video: HTMLVideoElement, effects: VfxEffect[], W: number, H: number) {
  if (effects.length === 0) { ctx.drawImage(video, 0, 0, W, H); return; }

  // Split: filter-compatible vs pixel-op effects
  const filterParts: string[] = [];
  const hasNeg  = effects.some(e => e.type === 'negative');
  const hasTint = effects.some(e => e.type === 'tint');
  const hasPx   = effects.some(e => e.type === 'pixelate');
  const hasChrom= effects.some(e => e.type === 'chromatic_aberration');
  const mirrorH = effects.some(e => e.type === 'mirror_h');
  const mirrorV = effects.some(e => e.type === 'mirror_v');

  for (const e of effects) {
    switch (e.type) {
      case 'blur':       filterParts.push(`blur(${e.params.radius}px)`); break;
      case 'grayscale':  filterParts.push('grayscale(1)'); break;
      case 'sepia':      filterParts.push('sepia(1)'); break;
      case 'brightness': filterParts.push(`brightness(${(1 + Number(e.params.value)).toFixed(2)})`); break;
      case 'contrast':   filterParts.push(`contrast(${Number(e.params.contrast ?? e.params.value).toFixed(2)})`); break;
      case 'saturation': filterParts.push(`saturate(${Number(e.params.value).toFixed(2)})`); break;
      default: break;
    }
  }

  ctx.save();
  if (mirrorH || mirrorV) {
    ctx.translate(mirrorH ? W : 0, mirrorV ? H : 0);
    ctx.scale(mirrorH ? -1 : 1, mirrorV ? -1 : 1);
  }
  if (filterParts.length) ctx.filter = filterParts.join(' ');
  ctx.drawImage(video, 0, 0, W, H);
  ctx.filter = 'none';
  ctx.restore();

  // Pixelate
  if (hasPx) {
    const px = effects.find(e => e.type === 'pixelate')!;
    const sz = Math.max(2, Number(px.params.size) || 16);
    const tmp = document.createElement('canvas');
    tmp.width = Math.max(1, Math.round(W / sz)); tmp.height = Math.max(1, Math.round(H / sz));
    const tCtx = tmp.getContext('2d')!;
    tCtx.imageSmoothingEnabled = false;
    tCtx.drawImage({ width: W, height: H } as any, 0, 0, tmp.width, tmp.height); // copy from ctx via ImageData
    const id = ctx.getImageData(0, 0, W, H);
    tCtx.putImageData(new ImageData(
      new Uint8ClampedArray(id.data.buffer.slice(0, id.data.length)), W, H
    ).data.length > 0 ? id : id, 0, 0); // same image
    tCtx.imageSmoothingEnabled = false;
    // Just re-draw to pixelated canvas then back
    const pxFull = document.createElement('canvas');
    pxFull.width = W; pxFull.height = H;
    const pCtx = pxFull.getContext('2d')!;
    pCtx.imageSmoothingEnabled = false;
    pCtx.drawImage(tmp, 0, 0, W, H);
    ctx.drawImage(pxFull, 0, 0, W, H);
  }

  // Chromatic aberration
  if (hasChrom) {
    const ch = effects.find(e => e.type === 'chromatic_aberration')!;
    const sh = Math.round(Number(ch.params.shift) || 10);
    const id = ctx.getImageData(0, 0, W, H);
    const d = id.data;
    const shifted = new Uint8ClampedArray(d.length);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const i = (y * W + x) * 4;
        const rX = Math.min(W - 1, x + sh), rI = (y * W + rX) * 4;
        const bX = Math.max(0, x - sh),      bI = (y * W + bX) * 4;
        shifted[i]     = d[rI];     // R shifted right
        shifted[i + 1] = d[i + 1]; // G unchanged
        shifted[i + 2] = d[bI + 2]; // B shifted left
        shifted[i + 3] = d[i + 3];
      }
    }
    ctx.putImageData(new ImageData(shifted, W, H), 0, 0);
  }

  // Negative / tint (pixel ops)
  if (hasNeg || hasTint) {
    const id = ctx.getImageData(0, 0, W, H);
    const d = id.data;
    if (hasNeg) {
      for (let i = 0; i < d.length; i += 4) { d[i] = 255-d[i]; d[i+1] = 255-d[i+1]; d[i+2] = 255-d[i+2]; }
    }
    if (hasTint) {
      const t = effects.find(e => e.type === 'tint')!;
      const { r, g, b } = hexToRgb(String(t.params.color || '#ff0000'));
      const op = Number(t.params.opacity || 0.4), inv = 1 - op;
      for (let i = 0; i < d.length; i += 4) {
        d[i] = d[i]*inv + r*op; d[i+1] = d[i+1]*inv + g*op; d[i+2] = d[i+2]*inv + b*op;
      }
    }
    ctx.putImageData(id, 0, 0);
  }

  // Vignette overlay
  const vig = effects.find(e => e.type === 'vignette');
  if (vig) {
    const intensity = Number(vig.params.intensity || 0.8);
    const grad = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, Math.max(W,H)*0.7);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, `rgba(0,0,0,${Math.min(0.97, intensity*0.75).toFixed(2)})`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  }
}

/**
 * Apply ALL standard VFX effects to existing canvas content (seg already rendered).
 * Full pipeline: pixel ops → canvas filters → spatial ops → overlays.
 */
function applyCanvasVfxOnCanvas(ctx: CanvasRenderingContext2D, effects: VfxEffect[], W: number, H: number) {
  if (effects.length === 0) return;

  // ── 1. Mirror / flip (spatial, must come first) ────────────────────────────
  const mirrorH = effects.some(e => e.type === 'mirror_h');
  const mirrorV = effects.some(e => e.type === 'mirror_v');
  if (mirrorH || mirrorV) {
    const tmp = document.createElement('canvas'); tmp.width = W; tmp.height = H;
    tmp.getContext('2d')!.drawImage(ctx.canvas, 0, 0);
    ctx.save();
    ctx.translate(mirrorH ? W : 0, mirrorV ? H : 0);
    ctx.scale(mirrorH ? -1 : 1, mirrorV ? -1 : 1);
    ctx.drawImage(tmp, 0, 0);
    ctx.restore();
  }

  // ── 2. Pixel-level ops: grayscale, sepia, negative, chromatic aberration ──
  const hasNeg   = effects.some(e => e.type === 'negative');
  const hasGray  = effects.some(e => e.type === 'grayscale');
  const hasSepia = effects.some(e => e.type === 'sepia');
  const hasChrom = effects.some(e => e.type === 'chromatic_aberration');
  const hasEdge  = effects.some(e => e.type === 'edge_detect');

  if (hasGray || hasSepia || hasNeg || hasChrom || hasEdge) {
    const id = ctx.getImageData(0, 0, W, H);
    const d  = id.data;

    if (hasGray || hasSepia) {
      for (let i = 0; i < d.length; i += 4) {
        const lum = 0.299*d[i] + 0.587*d[i+1] + 0.114*d[i+2];
        if (hasGray)  { d[i] = d[i+1] = d[i+2] = lum; }
        if (hasSepia) { d[i] = Math.min(255, lum*1.08+40); d[i+1] = Math.min(255, lum*0.93+20); d[i+2] = Math.min(255, lum*0.76); }
      }
    }
    if (hasNeg) {
      for (let i = 0; i < d.length; i += 4) { d[i] = 255-d[i]; d[i+1] = 255-d[i+1]; d[i+2] = 255-d[i+2]; }
    }
    if (hasChrom) {
      const sh = Math.round(Number(effects.find(e => e.type === 'chromatic_aberration')!.params.shift) || 10);
      const shifted = new Uint8ClampedArray(d.length);
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const i  = (y*W+x)*4;
          const rX = Math.min(W-1, x+sh), rI = (y*W+rX)*4;
          const bX = Math.max(0, x-sh),   bI = (y*W+bX)*4;
          shifted[i]   = d[rI];     shifted[i+1] = d[i+1];
          shifted[i+2] = d[bI+2];   shifted[i+3] = d[i+3];
        }
      }
      for (let i = 0; i < d.length; i++) d[i] = shifted[i];
    }
    if (hasEdge) {
      // Sobel edge detect on luminance
      const out = new Uint8ClampedArray(d.length);
      for (let y = 1; y < H-1; y++) {
        for (let x = 1; x < W-1; x++) {
          const lum = (px: number) => 0.299*d[px]+0.587*d[px+1]+0.114*d[px+2];
          const tl=lum((y-1)*W*4+(x-1)*4), t=lum((y-1)*W*4+x*4), tr=lum((y-1)*W*4+(x+1)*4);
          const ml=lum(y*W*4+(x-1)*4),                             mr=lum(y*W*4+(x+1)*4);
          const bl=lum((y+1)*W*4+(x-1)*4), b=lum((y+1)*W*4+x*4), br=lum((y+1)*W*4+(x+1)*4);
          const gx = -tl-2*ml-bl+tr+2*mr+br;
          const gy = -tl-2*t-tr+bl+2*b+br;
          const mag = Math.min(255, Math.sqrt(gx*gx+gy*gy));
          const i = (y*W+x)*4;
          out[i]=out[i+1]=out[i+2]=mag; out[i+3]=255;
        }
      }
      for (let i = 0; i < d.length; i++) d[i] = out[i];
    }
    ctx.putImageData(id, 0, 0);
  }

  // ── 3. Tint (pixel blend) ──────────────────────────────────────────────────
  const hasTint = effects.some(e => e.type === 'tint');
  if (hasTint) {
    const t = effects.find(e => e.type === 'tint')!;
    const {r,g,b} = hexToRgb(String(t.params.color || '#ff0000'));
    const op = Number(t.params.opacity || 0.4), inv = 1-op;
    const id = ctx.getImageData(0, 0, W, H); const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i] = d[i]*inv + r*op; d[i+1] = d[i+1]*inv + g*op; d[i+2] = d[i+2]*inv + b*op;
    }
    ctx.putImageData(id, 0, 0);
  }

  // ── 4. Canvas-filter ops: brightness / contrast / saturation / blur ────────
  const filterParts: string[] = [];
  effects.forEach(e => {
    if (e.type === 'brightness') filterParts.push(`brightness(${(1+Number(e.params.value)).toFixed(2)})`);
    if (e.type === 'contrast')   filterParts.push(`contrast(${Number(e.params.value).toFixed(2)})`);
    if (e.type === 'saturation') filterParts.push(`saturate(${Number(e.params.value).toFixed(2)})`);
    if (e.type === 'blur')       filterParts.push(`blur(${Number(e.params.radius)||8}px)`);
  });
  if (filterParts.length) {
    const tmp = document.createElement('canvas'); tmp.width = W; tmp.height = H;
    tmp.getContext('2d')!.drawImage(ctx.canvas, 0, 0);
    ctx.filter = filterParts.join(' ');
    ctx.drawImage(tmp, 0, 0);
    ctx.filter = 'none';
  }

  // ── 5. Pixelate ────────────────────────────────────────────────────────────
  const hasPx = effects.some(e => e.type === 'pixelate');
  if (hasPx) {
    const sz = Math.max(2, Number(effects.find(e => e.type === 'pixelate')!.params.size) || 16);
    const tmp = document.createElement('canvas');
    tmp.width = Math.max(1, Math.round(W/sz)); tmp.height = Math.max(1, Math.round(H/sz));
    const tCtx = tmp.getContext('2d')!; tCtx.imageSmoothingEnabled = false;
    tCtx.drawImage(ctx.canvas, 0, 0, tmp.width, tmp.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tmp, 0, 0, W, H);
    ctx.imageSmoothingEnabled = true;
  }

  // ── 6. Overlay effects: vignette, film_grain ───────────────────────────────
  const vig = effects.find(e => e.type === 'vignette');
  if (vig) {
    const intensity = Number(vig.params.intensity || 0.8);
    const grad = ctx.createRadialGradient(W/2, H/2, 0, W/2, H/2, Math.max(W,H)*0.7);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, `rgba(0,0,0,${Math.min(0.97, intensity*0.75).toFixed(2)})`);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
  }
  const grain = effects.find(e => e.type === 'film_grain');
  if (grain) {
    // Additive noise via random pixel dots
    const intensity = Number(grain.params.intensity || 0.4);
    const noiseAmt = Math.round(intensity * W * H * 0.3);
    ctx.save();
    ctx.globalAlpha = Math.min(0.75, intensity * 0.7);
    ctx.globalCompositeOperation = 'overlay';
    for (let n = 0; n < noiseAmt; n++) {
      const x = Math.random() * W, y = Math.random() * H;
      const v = Math.random() > 0.5 ? 255 : 0;
      ctx.fillStyle = `rgb(${v},${v},${v})`;
      ctx.fillRect(x, y, 1, 1);
    }
    ctx.restore();
  }
}

/**
 * Build an alpha-mask canvas from a MediaPipe Tasks Vision confidence mask.
 * confidenceMask.getAsFloat32Array() returns person-probability per pixel [0..1].
 * Returns null on error (callers fall back to plain video).
 */
function buildSegMaskCanvas(confidenceMask: any, W: number, H: number): HTMLCanvasElement | null {
  try {
    const data: Float32Array = confidenceMask.getAsFloat32Array();
    const maskW: number = confidenceMask.width;
    const maskH: number = confidenceMask.height;
    if (!maskW || !maskH || maskW <= 0 || maskH <= 0) return null;

    // Build ImageData at native mask resolution (256×256 for selfie_segmenter)
    const imgData = new ImageData(maskW, maskH);
    for (let i = 0; i < maskW * maskH; i++) {
      const prob = Math.max(0, Math.min(1, data[i]));
      imgData.data[i * 4 + 3] = Math.round(prob * 255); // alpha = person probability
    }

    const small = document.createElement('canvas');
    small.width = maskW; small.height = maskH;
    small.getContext('2d')!.putImageData(imgData, 0, 0);

    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    c.getContext('2d')!.drawImage(small, 0, 0, W, H);
    return c;
  } catch {
    return null;
  }
}

/**
 * Draw a foreground-only canvas: sharp video clipped to the seg alpha-mask.
 * Returns a canvas with the person visible and background transparent.
 * If mask is null, returns a transparent canvas (safe fallback).
 */
function buildForegroundCanvas(
  video: HTMLVideoElement,
  maskCanvas: HTMLCanvasElement | null,
  W: number, H: number,
): HTMLCanvasElement {
  const fg = document.createElement('canvas');
  fg.width = W; fg.height = H;
  if (!maskCanvas) return fg; // transparent — caller should decide fallback
  const fCtx = fg.getContext('2d')!;
  fCtx.drawImage(video, 0, 0, W, H);
  fCtx.globalCompositeOperation = 'destination-in';
  fCtx.drawImage(maskCanvas, 0, 0, W, H); // keep only where mask alpha > 0 (foreground)
  fCtx.globalCompositeOperation = 'source-over';
  return fg;
}

/**
 * Apply a segmentation effect on the canvas.
 * Takes a SegConfig item instead of a VfxZone.
 * segResult is an ImageSegmenterResult from @mediapipe/tasks-vision.
 * All operations are synchronous — no async/await needed.
 */
function applyCanvasSeg(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  item: SegConfig,
  segResult: any, // ImageSegmenterResult
  W: number, H: number,
  bgMedia?: HTMLImageElement | HTMLVideoElement | null,
) {
  const eff = item.segEffect;

  if (eff === 'background-blur') {
    const mask = buildSegMaskCanvas(segResult.confidenceMasks?.[0], W, H);
    ctx.filter = `blur(${item.segBlurRadius ?? 15}px)`;
    ctx.drawImage(video, 0, 0, W, H);
    ctx.filter = 'none';
    ctx.drawImage(buildForegroundCanvas(video, mask, W, H), 0, 0, W, H);

  } else if (eff === 'background-replace') {
    const mask = buildSegMaskCanvas(segResult.confidenceMasks?.[0], W, H);
    if (!mask) { ctx.drawImage(video, 0, 0, W, H); return; }
    if (bgMedia && item.segBgMediaType) {
      ctx.drawImage(bgMedia, 0, 0, W, H);
    } else {
      ctx.fillStyle = item.segBgColor ?? '#000000';
      ctx.fillRect(0, 0, W, H);
    }
    ctx.drawImage(buildForegroundCanvas(video, mask, W, H), 0, 0, W, H);

  } else if (eff === 'background-pixelate') {
    const px = Math.max(2, item.segPixelSize ?? 12);
    const tmp = document.createElement('canvas');
    tmp.width = Math.max(1, Math.round(W / px)); tmp.height = Math.max(1, Math.round(H / px));
    const tCtx = tmp.getContext('2d')!;
    tCtx.imageSmoothingEnabled = false;
    tCtx.drawImage(video, 0, 0, tmp.width, tmp.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tmp, 0, 0, W, H);
    ctx.imageSmoothingEnabled = true;
    const mask = buildSegMaskCanvas(segResult.confidenceMasks?.[0], W, H);
    ctx.drawImage(buildForegroundCanvas(video, mask, W, H), 0, 0, W, H);

  } else if (eff === 'silhouette') {
    const mask = buildSegMaskCanvas(segResult.confidenceMasks?.[0], W, H);
    if (!mask) { ctx.drawImage(video, 0, 0, W, H); return; }
    if (item.segSilhouetteOnVideo !== false) ctx.drawImage(video, 0, 0, W, H);
    else { ctx.fillStyle = '#000000'; ctx.fillRect(0, 0, W, H); }
    const sil = document.createElement('canvas');
    sil.width = W; sil.height = H;
    const sCtx = sil.getContext('2d')!;
    sCtx.fillStyle = item.segSilhouetteColor ?? '#ff3366';
    sCtx.fillRect(0, 0, W, H);
    sCtx.globalCompositeOperation = 'destination-in';
    sCtx.drawImage(mask, 0, 0, W, H);
    ctx.globalAlpha = item.segSilhouetteOpacity ?? 0.85;
    ctx.drawImage(sil, 0, 0, W, H);
    ctx.globalAlpha = 1;

  } else if (eff === 'body-parts') {
    // SelfieMulticlass: category mask — 0=bg,1=hair,2=body-skin,3=face-skin,4=clothes,5=accessories
    ctx.drawImage(video, 0, 0, W, H);
    const categoryMask = segResult.categoryMask;
    if (!categoryMask) return;
    const catData: Uint8Array = categoryMask.getAsUint8Array();
    const maskW: number = categoryMask.width;
    const maskH: number = categoryMask.height;
    if (!maskW || !maskH) return;

    const groups = item.segPartGroups ?? DEFAULT_SEG_PARTS;
    const anyBlur = (Object.keys(groups) as SegPartGroup[]).some(g => groups[g].enabled && groups[g].effect === 'blur');
    const anyPx   = (Object.keys(groups) as SegPartGroup[]).some(g => groups[g].enabled && groups[g].effect === 'pixelate');

    // Max blur intensity across enabled blur groups → scale blur radius (10→4px, 100→40px)
    const maxBlurIntensity = (Object.keys(groups) as SegPartGroup[])
      .filter(g => groups[g].enabled && groups[g].effect === 'blur')
      .reduce((m, g) => Math.max(m, groups[g].intensity), 10);
    const blurRadius = Math.round(maxBlurIntensity * 0.4);

    let blurredData: Uint8ClampedArray | null = null;
    if (anyBlur) {
      const bC = document.createElement('canvas'); bC.width = W; bC.height = H;
      const bCtx = bC.getContext('2d')!; bCtx.filter = `blur(${blurRadius}px)`;
      bCtx.drawImage(video, 0, 0, W, H); bCtx.filter = 'none';
      blurredData = bCtx.getImageData(0, 0, W, H).data;
    }
    let pixelatedData: Uint8ClampedArray | null = null;
    if (anyPx) {
      const maxPxIntensity = (Object.keys(groups) as SegPartGroup[])
        .filter(g => groups[g].enabled && groups[g].effect === 'pixelate')
        .reduce((m, g) => Math.max(m, groups[g].intensity), 10);
      const pxSize = Math.max(1, Math.round(W / Math.max(4, 80 - maxPxIntensity * 0.7)));
      const pC = document.createElement('canvas'); pC.width = pxSize; pC.height = Math.max(1, Math.round(H * pxSize / W));
      const pCtx = pC.getContext('2d')!; pCtx.imageSmoothingEnabled = false; pCtx.drawImage(video, 0, 0, pC.width, pC.height);
      const pF = document.createElement('canvas'); pF.width = W; pF.height = H;
      const pFCtx = pF.getContext('2d')!; pFCtx.imageSmoothingEnabled = false; pFCtx.drawImage(pC, 0, 0, W, H);
      pixelatedData = pFCtx.getImageData(0, 0, W, H).data;
    }

    const lookup = new Map<number, SegPartGroupConfig>();
    (Object.keys(groups) as SegPartGroup[]).forEach(g => {
      if (!groups[g].enabled) return;
      lookup.set(MULTICLASS_IDS[g], groups[g]);
    });
    if (lookup.size === 0) return;

    const imgData = ctx.getImageData(0, 0, W, H);
    const pixels = imgData.data;
    const scaleX = maskW / W, scaleY = maskH / H;

    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const mx = Math.min(maskW - 1, Math.round(x * scaleX));
        const my = Math.min(maskH - 1, Math.round(y * scaleY));
        const classId = catData[my * maskW + mx];
        const entry = lookup.get(classId);
        if (!entry) continue;
        const pi = (y * W + x) * 4, alpha = entry.intensity / 100;
        if (entry.effect === 'blur' && blurredData) {
          pixels[pi]   = pixels[pi]   * (1-alpha) + blurredData[pi]   * alpha;
          pixels[pi+1] = pixels[pi+1] * (1-alpha) + blurredData[pi+1] * alpha;
          pixels[pi+2] = pixels[pi+2] * (1-alpha) + blurredData[pi+2] * alpha;
        } else if (entry.effect === 'color') {
          const c = hexToRgb(entry.color);
          pixels[pi]   = pixels[pi]   * (1-alpha) + c.r * alpha;
          pixels[pi+1] = pixels[pi+1] * (1-alpha) + c.g * alpha;
          pixels[pi+2] = pixels[pi+2] * (1-alpha) + c.b * alpha;
        } else if (entry.effect === 'pixelate' && pixelatedData) {
          pixels[pi]   = pixels[pi]   * (1-alpha) + pixelatedData[pi]   * alpha;
          pixels[pi+1] = pixels[pi+1] * (1-alpha) + pixelatedData[pi+1] * alpha;
          pixels[pi+2] = pixels[pi+2] * (1-alpha) + pixelatedData[pi+2] * alpha;
        } else if (entry.effect === 'hide') {
          pixels[pi+3] = 0;
        }
      }
    }
    ctx.putImageData(imgData, 0, 0);
  }
}

// ── Props ──────────────────────────────────────────────────────────────────────

export interface GlobalSegConfig {
  segItems: SegConfig[];
}

export interface VfxConfig {
  globalEffects: VfxEffect[];
  zones: VfxZone[];
  globalSeg?: GlobalSegConfig;
}

export interface VideoEffectsEditorProps {
  file: FileItem;
  duration: number;
  initialGlobalEffects?: VfxEffect[];
  initialZones?: VfxZone[];
  initialGlobalSeg?: GlobalSegConfig;
  onPickFile?: (accept: 'image' | 'video', onSelected: (file: FileItem) => void) => void;
  onSave: (blob: Blob, filename: string, config: VfxConfig) => void;
  onCancel: () => void;
}

const DEFAULT_GLOBAL_SEG: GlobalSegConfig = { segItems: [] };

// ── Resize drag type ──────────────────────────────────────────────────────────
type ResizeDrag = { zoneId: string; edge: 'left' | 'right'; };

// ── Component ──────────────────────────────────────────────────────────────────

const VideoEffectsEditor: React.FC<VideoEffectsEditorProps> = ({ file, duration, initialGlobalEffects, initialZones, initialGlobalSeg, onPickFile, onSave, onCancel }) => {
  const videoRef    = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const [zones,         setZones]         = useState<VfxZone[]>(initialZones ?? []);
  const [globalEffects, setGlobalEffects] = useState<VfxEffect[]>(initialGlobalEffects ?? []);
  const [globalSeg,     setGlobalSeg]     = useState<GlobalSegConfig>(initialGlobalSeg ?? { ...DEFAULT_GLOBAL_SEG });
  const [selectedId,    setSelectedId]    = useState<string | null>(null); // null = global
  const [dragState,     setDragState]     = useState<{ startT: number; currentT: number } | null>(null);
  const [resizeDrag,    setResizeDrag]    = useState<ResizeDrag | null>(null);
  const [isExporting,   setIsExporting]   = useState(false);
  const [exportMsg,     setExportMsg]     = useState('');
  const [currentTime,   setCurrentTime]   = useState(0);
  const [addFxOpen,     setAddFxOpen]     = useState(false);
  const [segModelStatus, setSegModelStatus] = useState<'idle'|'loading'|'ready'|'error'>('idle');
  const zoneCounter        = useRef(0);
  const selfieSegRef       = useRef<any>(null); // @mediapipe/tasks-vision ImageSegmenter (selfie_segmenter)
  const multiclassSegRef   = useRef<any>(null); // @mediapipe/tasks-vision ImageSegmenter (selfie_multiclass_256x256)
  // ── Live preview canvas refs ─────────────────────────────────────────────────
  const previewCanvasRef   = useRef<HTMLCanvasElement>(null);
  const rafIdRef           = useRef<number>(0);
  const previewSegItemsRef = useRef<SegConfig[]>([]);
  const previewEffsRef     = useRef<VfxEffect[]>([]);
  const segBgImgRef        = useRef<HTMLImageElement | null>(null);
  const segBgVideoRef      = useRef<HTMLVideoElement | null>(null);
  const selectedIdRef      = useRef<string | null>(null); // always current — avoids stale closures
  const playheadDragging   = useRef(false);
  const isExportingRef     = useRef(false); // gates RAF preview loop during export

  // ── Seg model loader ───────────────────────────────────────────────────────

  const loadSegModel = useCallback(async (effect: SegEffect) => {
    const needMulti = effect === 'body-parts';
    const mRef = needMulti ? multiclassSegRef : selfieSegRef;
    if (mRef.current) return;
    setSegModelStatus('loading');
    try {
      // Load MJS bundle directly (webpackIgnore bypasses webpack bundling — pure browser ESM)
      const { ImageSegmenter, FilesetResolver } = await import(/* webpackIgnore: true */ '/vision_bundle.mjs' as any);
      const vision = await FilesetResolver.forVisionTasks(
        window.location.origin + '/mediapipe-wasm'
      );
      const modelAssetPath = needMulti
        ? 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite'
        : 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite';
      mRef.current = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: { modelAssetPath, delegate: 'CPU' },
        runningMode: 'VIDEO',
        outputCategoryMask: needMulti,
        outputConfidenceMasks: !needMulti,
      });
      setSegModelStatus('ready');
    } catch (e) { console.error('[MediaPipe] loadSegModel failed:', e); setSegModelStatus('error'); }
  }, []);

  // Keep selectedIdRef always current (avoids stale closures in callbacks/portals)
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // ── Active seg items for current selection ───────────────────────────────────

  const activeSegItems: SegConfig[] = selectedId === null
    ? (globalSeg.segItems ?? [])
    : (zones.find(z => z.id === selectedId)?.segItems ?? []);

  const addSegItem = useCallback(() => {
    const item = DEFAULT_SEG_ITEM();
    const sid = selectedIdRef.current;
    if (sid === null) setGlobalSeg(prev => ({ ...prev, segItems: [...prev.segItems, item] }));
    else setZones(prev => prev.map(z => z.id === sid ? { ...z, segItems: [...(z.segItems ?? []), item] } : z));
    loadSegModel(item.segEffect);
  }, [loadSegModel]);

  const updateSegItem = useCallback((itemId: string, patch: Partial<SegConfig>) => {
    const sid = selectedIdRef.current;
    const upd = (items: SegConfig[]) => items.map(s => s.id === itemId ? { ...s, ...patch } : s);
    if (sid === null) setGlobalSeg(prev => ({ ...prev, segItems: upd(prev.segItems) }));
    else setZones(prev => prev.map(z => z.id === sid ? { ...z, segItems: upd(z.segItems ?? []) } : z));
  }, []);

  const removeSegItem = useCallback((itemId: string) => {
    const sid = selectedIdRef.current;
    if (sid === null) setGlobalSeg(prev => ({ ...prev, segItems: prev.segItems.filter(s => s.id !== itemId) }));
    else setZones(prev => prev.map(z => z.id === sid ? { ...z, segItems: (z.segItems ?? []).filter(s => s.id !== itemId) } : z));
  }, []);

  // ── Active effect chain ──────────────────────────────────────────────────────

  const activeEffects: VfxEffect[] = selectedId === null
    ? globalEffects
    : (zones.find(z => z.id === selectedId)?.effects ?? []);

  const setActiveEffects = useCallback((updater: (prev: VfxEffect[]) => VfxEffect[]) => {
    if (selectedId === null) {
      setGlobalEffects(updater);
    } else {
      setZones(prev => prev.map(z =>
        z.id === selectedId ? { ...z, effects: updater(z.effects) } : z
      ));
    }
  }, [selectedId]);

  // ── CSS preview (approximate) ────────────────────────────────────────────────

  const activePreviewEffects = useMemo(() => {
    const t = currentTime;
    const zone = zones.find(z => t >= z.start && t < z.end);
    return [...globalEffects, ...(zone?.effects ?? [])];
  }, [currentTime, zones, globalEffects]);

  const previewFilter    = useMemo(() => toCssFilter(activePreviewEffects),    [activePreviewEffects]);
  const previewTransform = useMemo(() => toCssTransform(activePreviewEffects), [activePreviewEffects]);
  const previewOverlays  = useMemo(() => getEffectOverlays(activePreviewEffects), [activePreviewEffects]);

  // ── Real-time seg preview ─────────────────────────────────────────────────────

  /** Seg items active at current playhead (zone-specific or global). */
  const activePreviewSegItems = useMemo((): SegConfig[] => {
    const zone = zones.find(z => currentTime >= z.start && currentTime < z.end);
    if (zone?.segItems?.length) return zone.segItems;
    if (globalSeg.segItems?.length) return globalSeg.segItems;
    return [];
  }, [currentTime, zones, globalSeg]);

  const segPreviewActive = activePreviewSegItems.length > 0 && segModelStatus === 'ready';

  // Keep refs in sync so RAF loop reads latest values without restarting
  useEffect(() => { previewSegItemsRef.current = activePreviewSegItems; }, [activePreviewSegItems]);
  useEffect(() => { previewEffsRef.current = activePreviewEffects; }, [activePreviewEffects]);

  // Load background image/video for seg preview (first item with bgMediaUrl)
  useEffect(() => {
    const url = activePreviewSegItems[0]?.segBgMediaUrl;
    const type = activePreviewSegItems[0]?.segBgMediaType;
    segBgImgRef.current = null;
    if (segBgVideoRef.current) { segBgVideoRef.current.pause(); segBgVideoRef.current = null; }
    if (!url || !type) return;
    if (type === 'image') {
      const img = new Image();
      img.onload = () => { segBgImgRef.current = img; };
      img.src = url;
    } else if (type === 'video') {
      const vid = document.createElement('video');
      vid.src = url; vid.loop = true; vid.muted = true; vid.playsInline = true;
      vid.play().catch(() => {});
      segBgVideoRef.current = vid;
    }
    return () => {
      segBgImgRef.current = null;
      if (segBgVideoRef.current) { segBgVideoRef.current.pause(); segBgVideoRef.current = null; }
    };
  }, [activePreviewSegItems]); // watch full array — bg media load only uses first item's url/type

  // Start/stop RAF loop when model becomes ready or seg items change
  useEffect(() => {
    const canvas = previewCanvasRef.current;
    const video  = videoRef.current;
    cancelAnimationFrame(rafIdRef.current);
    if (!canvas || !video || activePreviewSegItems.length === 0 || segModelStatus !== 'ready') return;

    // Check we have at least one model loaded for the items
    const hasAnyModel = activePreviewSegItems.some(item => {
      const needMulti = item.segEffect === 'body-parts';
      return needMulti ? !!multiclassSegRef.current : !!selfieSegRef.current;
    });
    if (!hasAnyModel) return;

    let alive = true;

    const loop = () => {
      if (!alive) return;
      // Pause preview RAF during export — avoid timestamp collisions on the same MediaPipe model
      if (isExportingRef.current) { rafIdRef.current = requestAnimationFrame(loop); return; }
      const W = video.videoWidth;
      const H = video.videoHeight;
      if (video.readyState >= 2 && W > 0 && H > 0) {
        if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
        const ctx = canvas.getContext('2d')!;
        const items = previewSegItemsRef.current;
        try {
          if (items.length > 0) {
            ctx.clearRect(0, 0, W, H);
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              const needMulti = item.segEffect === 'body-parts';
              const model = needMulti ? multiclassSegRef.current : selfieSegRef.current;
              if (!model) continue;
              const result = model.segmentForVideo(video, performance.now());
              const bgMedia = i === 0 ? (segBgImgRef.current ?? segBgVideoRef.current ?? undefined) : undefined;
              applyCanvasSeg(ctx, video, item, result, W, H, bgMedia);
              result.close();
            }
            // Apply ALL standard effects on top of seg canvas (grayscale, blur, sepia, etc.)
            const effs = previewEffsRef.current;
            if (effs.length > 0) applyCanvasVfxOnCanvas(ctx, effs, W, H);
          } else {
            ctx.drawImage(video, 0, 0, W, H);
          }
        } catch {
          ctx.drawImage(video, 0, 0, W, H);
        }
      }
      if (alive) rafIdRef.current = requestAnimationFrame(loop);
    };

    rafIdRef.current = requestAnimationFrame(loop);
    return () => { alive = false; cancelAnimationFrame(rafIdRef.current); };
  }, [segModelStatus, activePreviewSegItems.length]); // deps intentionally limited: param changes handled via refs

  // ── Timeline drag ────────────────────────────────────────────────────────────

  const getTimeFromX = useCallback((clientX: number) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect || !duration) return 0;
    return Math.max(0, Math.min(duration, ((clientX - rect.left) / rect.width) * duration));
  }, [duration]);

  const handlePlayheadMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    playheadDragging.current = true;
  }, []);

  const handleTimelineMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).dataset.zoneId) return;
    const t = getTimeFromX(e.clientX);
    setDragState({ startT: t, currentT: t });
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
    e.preventDefault();
  }, [getTimeFromX]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (playheadDragging.current) {
      const t = getTimeFromX(e.clientX);
      if (videoRef.current) videoRef.current.currentTime = t;
      setCurrentTime(t);
      return;
    }
    if (resizeDrag) {
      const t = getTimeFromX(e.clientX);
      setZones(prev => prev.map(z => {
        if (z.id !== resizeDrag.zoneId) return z;
        if (resizeDrag.edge === 'left')  return { ...z, start: Math.max(0, Math.min(t, z.end - 0.1)) };
        if (resizeDrag.edge === 'right') return { ...z, end: Math.max(z.start + 0.1, Math.min(t, duration)) };
        return z;
      }));
      return;
    }
    if (!dragState) return;
    setDragState(d => d ? { ...d, currentT: getTimeFromX(e.clientX) } : null);
  }, [dragState, resizeDrag, getTimeFromX, duration]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (playheadDragging.current) {
      playheadDragging.current = false;
      return;
    }
    if (resizeDrag) { setResizeDrag(null); return; }
    if (!dragState) return;
    const endT = getTimeFromX(e.clientX);
    const start = Math.min(dragState.startT, endT);
    const end   = Math.max(dragState.startT, endT);
    if (end - start > 0.15) {
      const id    = uid();
      const color = ZONE_COLORS[zoneCounter.current % ZONE_COLORS.length];
      zoneCounter.current++;
      setZones(prev => [...prev, { id, start, end, color, label: `Zone ${zoneCounter.current}`, effects: [] }]);
      setSelectedId(id);
    }
    setDragState(null);
  }, [dragState, resizeDrag, getTimeFromX]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // ── Effect chain ops ─────────────────────────────────────────────────────────

  const addEffect = (type: VfxEffectType) => {
    setActiveEffects(prev => [...prev, { id: uid(), type, params: defaultParams(type) }]);
    setAddFxOpen(false);
  };

  const removeEffect = (id: string) =>
    setActiveEffects(prev => prev.filter(e => e.id !== id));

  const moveEffect = (id: string, dir: -1 | 1) =>
    setActiveEffects(prev => {
      const idx = prev.findIndex(e => e.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });

  const updateParam = (effectId: string, key: string, val: number | string) =>
    setActiveEffects(prev => prev.map(e =>
      e.id === effectId ? { ...e, params: { ...e.params, [key]: val } } : e
    ));

  // ── Export ───────────────────────────────────────────────────────────────────

  const handleExport = async () => {
    const hasStdEffects = globalEffects.length > 0 || zones.some(z => z.effects.length > 0);
    const hasSegEffects = (globalSeg.segItems?.length ?? 0) > 0 || zones.some(z => (z.segItems?.length ?? 0) > 0);
    if (!hasStdEffects && !hasSegEffects) { alert('Aucun effet configuré.'); return; }

    setIsExporting(true);
    isExportingRef.current = true; // stop preview RAF to avoid MediaPipe timestamp collisions

    // ── Canvas export (when global or any zone has seg effect) ────────────────
    if (hasSegEffects) {
      try {
        setExportMsg('Chargement des modèles IA…');
        // Load all needed models
        const allSegItems = [
          ...(globalSeg.segItems ?? []),
          ...zones.flatMap(z => z.segItems ?? []),
        ];
        const needsBP = allSegItems.some(s => s.segEffect === 'body-parts');
        const needsMP = allSegItems.some(s => s.segEffect !== 'body-parts');
        if (needsBP) await loadSegModel('body-parts');
        if (needsMP) await loadSegModel('background-blur');

        const video = videoRef.current!;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const W = canvas.width, H = canvas.height;
        const ctx = canvas.getContext('2d')!;

        // Audio capture
        const audioCtx = new AudioContext();
        const src = audioCtx.createMediaElementSource(video);
        const audioDest = audioCtx.createMediaStreamDestination();
        src.connect(audioDest); src.connect(audioCtx.destination);

        const canvasStream = (canvas as any).captureStream(30);
        const combined = new MediaStream([...canvasStream.getVideoTracks(), ...audioDest.stream.getAudioTracks()]);
        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm';
        const recorder = new MediaRecorder(combined, { mimeType });
        const chunks: BlobPart[] = [];
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
        recorder.start(200);

        video.currentTime = 0;
        await video.play();
        setExportMsg('Export canvas en cours…');

        const dur = video.duration || 30;
        // Timestamps for segmentForVideo must be strictly monotonically increasing (milliseconds).
        // Start 10 minutes ahead of any live-preview performance.now() to avoid collisions.
        // Increment by 34ms per frame (~29fps), plus 1ms per seg-item within a frame.
        let exportTs = Math.round(performance.now()) + 600_000;

        await new Promise<void>(resolve => {
          const processFrame = () => {
            if (video.ended || video.currentTime >= dur - 0.1) { resolve(); return; }
            const t = video.currentTime;

            const activeZone = zones.find(z => t >= z.start && t < z.end) ?? null;
            const zoneEffects = activeZone?.effects ?? [];
            const allEffects = [...globalEffects, ...zoneEffects];

            // Determine active seg items: zone-specific takes priority over global
            const activeZoneSegItems = activeZone?.segItems ?? [];
            const activeGlobalSegItems = globalSeg.segItems ?? [];
            const activeSegItemsForFrame = activeZoneSegItems.length > 0 ? activeZoneSegItems : activeGlobalSegItems;

            // Advance frame timestamp once per video frame
            exportTs += 34;

            if (activeSegItemsForFrame.length > 0) {
              try {
                ctx.clearRect(0, 0, W, H);
                for (let si = 0; si < activeSegItemsForFrame.length; si++) {
                  const item = activeSegItemsForFrame[si];
                  const needMulti = item.segEffect === 'body-parts';
                  const model = needMulti ? multiclassSegRef.current : selfieSegRef.current;
                  if (!model) continue;
                  // Each item within the same frame gets +1ms to stay strictly monotonic
                  const result = model.segmentForVideo(video, exportTs + si);
                  const bgMedia = segBgImgRef.current ?? segBgVideoRef.current ?? undefined;
                  applyCanvasSeg(ctx, video, item, result, W, H, bgMedia);
                  result.close();
                }
                if (allEffects.length > 0) applyCanvasVfxOnCanvas(ctx, allEffects, W, H);
              } catch (segErr) {
                console.warn('[VFX export] seg frame failed, fallback to plain VFX:', segErr);
                applyCanvasVfx(ctx, video, allEffects, W, H);
              }
            } else {
              applyCanvasVfx(ctx, video, allEffects, W, H);
            }

            setExportMsg(`Export IA… ${Math.round((t / dur) * 100)}%`);
            requestAnimationFrame(processFrame);
          };
          video.onended = () => resolve();
          requestAnimationFrame(processFrame);
        });

        video.pause();
        recorder.stop();
        audioCtx.close();

        const blob = await new Promise<Blob>(res => { recorder.onstop = () => res(new Blob(chunks, { type: mimeType })); });
        const outName = (file.name || 'video').replace(/\.[^.]+$/, '') + '_effets.webm';
        onSave(blob, outName, { globalEffects, zones, globalSeg });
      } catch (e: any) {
        alert('Export IA échoué : ' + e.message);
      } finally {
        setIsExporting(false); isExportingRef.current = false; setExportMsg('');
      }
      return;
    }

    // ── FFmpeg export (standard effects only) ─────────────────────────────────
    try {
      setExportMsg('Chargement de la vidéo…');
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
      const srcResp = await fetch(file.url, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (!srcResp.ok) throw new Error(`Impossible de charger la vidéo (${srcResp.status})`);
      const srcBlob = await srcResp.blob();

      setExportMsg('Application des effets FFmpeg…');
      const form = new FormData();
      form.append('video', srcBlob, file.name || 'video.mp4');
      form.append('config', JSON.stringify({ globalEffects, zones, duration }));

      const resp = await fetch('/api/video/apply-effects', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: 'Erreur serveur' }));
        throw new Error(err.message || 'Erreur serveur');
      }
      const outBlob = await resp.blob();
      const outName = (file.name || 'video').replace(/\.[^.]+$/, '') + '_effets.mp4';
      onSave(outBlob, outName, { globalEffects, zones, globalSeg });
    } catch (e: any) {
      alert('Export échoué : ' + e.message);
    } finally {
      setIsExporting(false); setExportMsg('');
    }
  };

  // ── Drag preview bounds ──────────────────────────────────────────────────────

  const dragMin = dragState && duration ? Math.min(dragState.startT, dragState.currentT) / duration : 0;
  const dragMax = dragState && duration ? Math.max(dragState.startT, dragState.currentT) / duration : 0;

  // ── Styles ───────────────────────────────────────────────────────────────────

  const S = {
    overlay:   { position: 'fixed', inset: 0, zIndex: 999999, background: '#070b14', display: 'flex', flexDirection: 'column', color: '#e2e8f0', fontFamily: 'system-ui, sans-serif' } as React.CSSProperties,
    header:    { display: 'flex', alignItems: 'center', padding: '11px 18px', background: '#0f172a', borderBottom: '1px solid #1e293b', gap: 10, flexShrink: 0 } as React.CSSProperties,
    timeline:  { padding: '10px 16px', background: '#0a0f1e', borderBottom: '1px solid #1e293b', flexShrink: 0 } as React.CSSProperties,
    topArea:   { display: 'flex', flex: 1, overflow: 'hidden' } as React.CSSProperties,
    label:     { fontSize: 10, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4 },
    panel:     { background: '#0f172a', borderRight: '1px solid #1e293b', display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' },
    footer:    { display: 'flex', alignItems: 'center', padding: '10px 16px', background: '#0f172a', borderTop: '1px solid #1e293b', gap: 10, flexShrink: 0 } as React.CSSProperties,
    btn:       { background: '#334155', color: '#e2e8f0', border: '1px solid #475569', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
    btnPrimary:{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700 } as React.CSSProperties,
  };

  const hasSegEffectsFooter = (globalSeg.segItems?.length ?? 0) > 0 || zones.some(z => (z.segItems?.length ?? 0) > 0);

  // ── Render ───────────────────────────────────────────────────────────────────

  return createPortal(
    <div style={S.overlay} onClick={() => setAddFxOpen(false)}>

      {/* Header */}
      <div style={S.header}>
        <span style={{ fontSize: 20 }}>🎬</span>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Effets Vidéo</span>
        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }}>— {file.name}</span>
        <div style={{ flex: 1 }} />
      </div>

      {/* Timeline */}
      <div style={S.timeline}>
        <div style={S.label}>TIMELINE — clic pour positionner · glisser pour créer une zone d'effet</div>
        <div
          ref={timelineRef}
          style={{ position: 'relative', height: 56, background: '#1e293b', borderRadius: 6, cursor: 'crosshair', userSelect: 'none', overflow: 'hidden' }}
          onMouseDown={handleTimelineMouseDown}
        >
          {/* Time markers */}
          {duration > 0 && Array.from({ length: Math.floor(duration) + 1 }, (_, i) => i).map(t => (
            <div key={t} style={{ position: 'absolute', left: `${(t / duration) * 100}%`, top: 0, bottom: 0, width: 1, background: '#ef4444', pointerEvents: 'none' }}>
              <span style={{ position: 'absolute', top: 2, left: 3, fontSize: 9, color: '#ef4444' }}>{fmt(t)}</span>
            </div>
          ))}

          {/* Zones */}
          {zones.map(z => (
            <div
              key={z.id}
              data-zone-id={z.id}
              onClick={e => { e.stopPropagation(); setSelectedId(z.id); if (videoRef.current) videoRef.current.currentTime = z.start; }}
              style={{
                position: 'absolute',
                left: `${(z.start / duration) * 100}%`,
                width: `${((z.end - z.start) / duration) * 100}%`,
                top: 0, bottom: 0,
                background: z.color,
                borderLeft: `2px solid ${z.color.replace('0.5', '1')}`,
                borderRight: `2px solid ${z.color.replace('0.5', '1')}`,
                outline: selectedId === z.id ? '2px solid #facc15' : 'none',
                outlineOffset: -1,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', paddingLeft: 5, overflow: 'hidden',
              }}
            >
              {/* Left resize handle */}
              <div
                onMouseDown={e => { e.stopPropagation(); setResizeDrag({ zoneId: z.id, edge: 'left' }); e.preventDefault(); }}
                style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.3)', zIndex: 2 }}
              />
              <span style={{ fontSize: 9, color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', pointerEvents: 'none' }}>{z.label}</span>
              {/* Right resize handle */}
              <div
                onMouseDown={e => { e.stopPropagation(); setResizeDrag({ zoneId: z.id, edge: 'right' }); e.preventDefault(); }}
                style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'ew-resize', background: 'rgba(255,255,255,0.3)', zIndex: 2 }}
              />
            </div>
          ))}

          {/* Drag preview */}
          {dragState && (
            <div style={{ position: 'absolute', left: `${dragMin * 100}%`, width: `${(dragMax - dragMin) * 100}%`, top: 0, bottom: 0, background: 'rgba(250,204,21,0.2)', border: '1px dashed #facc15', pointerEvents: 'none' }} />
          )}

          {/* Playhead */}
          {duration > 0 && (
            <>
              {/* Draggable handle — triangle at top */}
              <div
                onMouseDown={handlePlayheadMouseDown}
                style={{ position: 'absolute', left: `${(currentTime / duration) * 100}%`, top: 0, transform: 'translateX(-6px)', width: 12, height: 56, cursor: 'ew-resize', zIndex: 12 }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '10px solid #facc15' }} />
              </div>
              {/* Playhead line */}
              <div style={{ position: 'absolute', left: `${(currentTime / duration) * 100}%`, top: 0, bottom: 0, width: 2, background: '#facc15', pointerEvents: 'none', zIndex: 11 }} />
            </>
          )}
        </div>
      </div>

      {/* Main area */}
      <div style={S.topArea}>

        {/* Left: video preview */}
        <div style={{ width: '42%', flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#020617', padding: 12, gap: 8, borderRight: '1px solid #1e293b' }}>
          <div style={S.label}>
            {segPreviewActive ? '🤖 PRÉVISUALISATION IA — TEMPS RÉEL' : 'PRÉVISUALISATION (approximative — CSS)'}
          </div>
          <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', borderRadius: 8, overflow: 'hidden', minHeight: 0 }}>
            {/* Video — always visible; canvas overlays it when seg is active */}
            <video
              ref={videoRef}
              src={file.url}
              style={{
                maxWidth: '100%', maxHeight: '100%', display: 'block',
                filter: previewFilter,
                transform: previewTransform,
                transition: 'filter 0.15s, transform 0.15s',
              }}
              onTimeUpdate={e => setCurrentTime((e.target as HTMLVideoElement).currentTime)}
              controls
            />
            {/* CSS overlay effects (tint, vignette, grain) — only when no seg canvas */}
            {!segPreviewActive && previewOverlays.map((style, i) => <div key={i} style={style} />)}
            {/* Seg canvas — absolutely positioned to cover the video; pointer-events: none so video controls still work */}
            <canvas
              ref={previewCanvasRef}
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '100%', maxHeight: '100%',
                display: segPreviewActive ? 'block' : 'none',
                pointerEvents: 'none',
              }}
            />
            {/* Loading indicator while model initializes */}
            {activePreviewSegItems.length > 0 && segModelStatus === 'loading' && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', flexDirection: 'column', gap: 10 }}>
                <div style={{ width: 28, height: 28, border: '3px solid #8b5cf6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'vfxSpin 0.8s linear infinite' }} />
                <span style={{ fontSize: 12, color: '#c4b5fd' }}>Chargement du modèle IA…</span>
              </div>
            )}
            {/* Hint overlay when seg preview is active */}
            {segPreviewActive && (
              <div style={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.65)', color: '#c4b5fd', fontSize: 10, padding: '3px 8px', borderRadius: 4, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                🤖 IA temps réel · cliquer sur la vidéo pour ▶/⏸
              </div>
            )}
          </div>
          <div style={{ fontSize: 10, color: '#475569', textAlign: 'center', lineHeight: 1.4 }}>
            {segPreviewActive
              ? '🤖 Rendu IA temps réel. L\'export final applique le même traitement image par image.'
              : 'Aperçu CSS approximatif. Le rendu final (FFmpeg) sera précis pixel par pixel.'}
          </div>
        </div>

        {/* Center: zone list */}
        <div style={{ ...S.panel, width: 210, flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #1e293b' }}>
            <div style={S.label}>ZONES</div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* Global */}
            <div
              onClick={() => setSelectedId(null)}
              style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: selectedId === null ? '#1e3a5f' : 'transparent', borderLeft: `3px solid ${selectedId === null ? '#6366f1' : 'transparent'}` }}
            >
              <span style={{ fontSize: 14 }}>🌍</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: selectedId === null ? 700 : 400 }}>Global (toute la vidéo)</div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{globalEffects.length} effet(s){(globalSeg.segItems?.length ?? 0) > 0 ? ' · 🤖 IA' : ''}</div>
              </div>
            </div>

            {/* Zones */}
            {zones.map(z => (
              <div
                key={z.id}
                onClick={() => { setSelectedId(z.id); if (videoRef.current) videoRef.current.currentTime = z.start; }}
                style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: selectedId === z.id ? '#1e3a5f' : 'transparent', borderLeft: `3px solid ${selectedId === z.id ? z.color.replace('0.5', '1') : 'transparent'}` }}
              >
                <div style={{ width: 10, height: 10, borderRadius: 2, background: z.color.replace('0.5', '0.9'), flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: selectedId === z.id ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{z.label}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>{fmt(z.start)} → {fmt(z.end)}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>
                    {z.effects.length + (z.segItems?.length ?? 0)} effet(s){(z.segItems?.length ?? 0) > 0 ? ' · 🤖 IA' : ''}
                  </div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); setZones(prev => prev.filter(x => x.id !== z.id)); if (selectedId === z.id) setSelectedId(null); }}
                  style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}
                  title="Supprimer la zone"
                >✕</button>
              </div>
            ))}

            {zones.length === 0 && (
              <div style={{ fontSize: 11, color: '#334155', padding: '12px 14px', lineHeight: 1.5 }}>
                Glisser sur la timeline pour créer une zone.
              </div>
            )}
          </div>
        </div>

        {/* Right: effect chain */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Effets — {selectedId === null ? 'Global' : zones.find(z => z.id === selectedId)?.label ?? 'Zone'}
            </span>
            <div style={{ flex: 1 }} />
            <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button onClick={() => setAddFxOpen(x => !x)} style={{ ...S.btn, fontSize: 11, background: '#1e293b' }}>+ Ajouter un effet</button>
              {addFxOpen && (
                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 6, display: 'flex', flexDirection: 'column', gap: 2, width: 230, maxHeight: 340, overflowY: 'auto', marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  {(Object.entries(VFX_DEFS) as [VfxEffectType, typeof VFX_DEFS[VfxEffectType]][]).map(([type, def]) => (
                    <button
                      key={type}
                      onClick={() => addEffect(type)}
                      style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '7px 10px', textAlign: 'left', fontSize: 12, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#334155')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      <span style={{ fontSize: 14, width: 20 }}>{def.emoji}</span>
                      <span>{def.label}</span>
                    </button>
                  ))}
                  <div style={{ height: 1, background: '#334155', margin: '4px 0' }} />
                  <button
                    onClick={() => { addSegItem(); setAddFxOpen(false); }}
                    style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '7px 10px', textAlign: 'left' as const, fontSize: 12, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#334155'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                  >
                    <span style={{ fontSize: 14, width: 20 }}>🤖</span>
                    <span>Segmentation IA</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activeEffects.length === 0 && activeSegItems.length === 0 && (
              <div style={{ textAlign: 'center', color: '#334155', fontSize: 12, padding: '28px 0', lineHeight: 1.6 }}>
                {selectedId === null
                  ? 'Aucun effet global.\nCes effets s\'appliquent à toute la vidéo.'
                  : 'Aucun effet pour cette zone.\nCes effets s\'appliquent uniquement à la plage sélectionnée.'}
              </div>
            )}

            {activeEffects.map((eff, idx) => {
              const def = VFX_DEFS[eff.type];
              return (
                <div key={eff.id} style={{ background: '#0a1628', border: '1px solid #1e3a5f', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: def.params.length ? 10 : 0 }}>
                    <span style={{ fontSize: 16 }}>{def.emoji}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, flex: 1, color: '#e2e8f0' }}>{def.label}</span>
                    <button onClick={() => moveEffect(eff.id, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', color: idx === 0 ? '#1e293b' : '#64748b', cursor: idx === 0 ? 'default' : 'pointer', fontSize: 14, padding: '0 3px' }}>↑</button>
                    <button onClick={() => moveEffect(eff.id,  1)} disabled={idx === activeEffects.length - 1} style={{ background: 'none', border: 'none', color: idx === activeEffects.length - 1 ? '#1e293b' : '#64748b', cursor: idx === activeEffects.length - 1 ? 'default' : 'pointer', fontSize: 14, padding: '0 3px' }}>↓</button>
                    <button onClick={() => removeEffect(eff.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 15, padding: '0 3px' }}>✕</button>
                  </div>

                  {def.params.map((pd: ParamDef) => (
                    <div key={pd.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: 11, color: '#64748b', width: 78, flexShrink: 0 }}>{pd.label}</span>
                      {pd.type === 'range' ? (
                        <>
                          <input
                            type="range" min={pd.min} max={pd.max} step={pd.step}
                            value={Number(eff.params[pd.key] ?? pd.default)}
                            onChange={ev => updateParam(eff.id, pd.key, Number(ev.target.value))}
                            style={{ flex: 1, accentColor: '#6366f1' }}
                          />
                          <span style={{ fontSize: 11, color: '#94a3b8', width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {Number(eff.params[pd.key] ?? pd.default).toFixed(
                              (pd.step ?? 1) < 0.1 ? 2 : (pd.step ?? 1) < 1 ? 1 : 0
                            )}
                          </span>
                        </>
                      ) : (
                        <input
                          type="color"
                          value={String(eff.params[pd.key] ?? pd.default)}
                          onChange={ev => updateParam(eff.id, pd.key, ev.target.value)}
                          style={{ width: 40, height: 26, padding: 2, background: '#1e293b', border: '1px solid #475569', borderRadius: 4, cursor: 'pointer' }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* ── Segmentation IA cards in effects list ──────────────────────── */}
            {activeSegItems.map((item, idx) => (
              <div key={item.id} style={{ background: '#0a1628', border: '1px solid #2d1b69', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>🤖</span>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, color: '#c4b5fd' }}>Segmentation IA {activeSegItems.length > 1 ? `#${idx+1}` : ''}</span>
                  {segModelStatus === 'loading' && idx === 0 && <span style={{ fontSize: 11, color: '#fbbf24' }}>⏳ chargement…</span>}
                  {segModelStatus === 'ready' && <span style={{ fontSize: 11, color: '#4ade80' }}>⬤ prêt</span>}
                  {segModelStatus === 'error' && <span style={{ fontSize: 11, color: '#f87171' }}>⬤ erreur</span>}
                  <button onClick={() => removeSegItem(item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 15, padding: '0 3px' }}>✕</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Effect type selector */}
                  <div>
                    <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 6 }}>Type d'effet IA</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {(Object.keys(SEG_EFFECT_LABELS) as SegEffect[]).map(eff => (
                        <button
                          key={eff}
                          onClick={() => { updateSegItem(item.id, { segEffect: eff }); loadSegModel(eff); }}
                          style={{ textAlign: 'left' as const, padding: '6px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, background: item.segEffect === eff ? '#4c1d95' : '#1e293b', color: item.segEffect === eff ? '#fff' : '#94a3b8', fontWeight: item.segEffect === eff ? 700 : 400 }}
                        >
                          {SEG_EFFECT_LABELS[eff]}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* background-blur params */}
                  {item.segEffect === 'background-blur' && (
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Intensité flou : {item.segBlurRadius}px</div>
                      <input type="range" min={2} max={40} value={item.segBlurRadius} style={{ width: '100%', accentColor: '#8b5cf6' }}
                        onChange={e => updateSegItem(item.id, { segBlurRadius: +e.target.value })} />
                    </div>
                  )}

                  {/* background-replace: color or image/video */}
                  {item.segEffect === 'background-replace' && (() => {
                    const mediaType = item.segBgMediaType;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Fond de remplacement</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {(['color', 'image', 'video'] as const).map(t => (
                            <button
                              key={t}
                              onClick={() => {
                                if (t === 'color') {
                                  updateSegItem(item.id, { segBgMediaType: undefined, segBgMediaUrl: undefined });
                                } else {
                                  updateSegItem(item.id, { segBgMediaType: t });
                                  if (onPickFile) onPickFile(t, f => updateSegItem(item.id, { segBgMediaUrl: f.url }));
                                }
                              }}
                              style={{ flex: 1, padding: '4px 6px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, background: (!mediaType && t === 'color') || mediaType === t ? '#4c1d95' : '#1e293b', color: '#e2e8f0' }}
                            >
                              {t === 'color' ? '🎨 Couleur' : t === 'image' ? '🖼️ Image' : '🎬 Vidéo'}
                            </button>
                          ))}
                        </div>
                        {!mediaType && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="color" value={item.segBgColor}
                              onChange={e => updateSegItem(item.id, { segBgColor: e.target.value })}
                              style={{ width: 48, height: 32, padding: 2, background: '#1e293b', border: '1px solid #475569', borderRadius: 4, cursor: 'pointer' }} />
                            <span style={{ fontSize: 11, color: '#64748b' }}>{item.segBgColor}</span>
                          </div>
                        )}
                        {mediaType && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {onPickFile && (
                              <button onClick={() => onPickFile(mediaType, f => updateSegItem(item.id, { segBgMediaUrl: f.url }))}
                                style={{ fontSize: 11, background: '#334155', border: '1px solid #475569', color: '#e2e8f0', borderRadius: 4, padding: '5px 12px', cursor: 'pointer' }}>
                                📂 Changer le fichier
                              </button>
                            )}
                            {!onPickFile && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                <span style={{ fontSize: 11, color: '#94a3b8' }}>Fichier local :</span>
                                <input type="file" accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                                  onChange={e => { const f = e.target.files?.[0]; if (f) updateSegItem(item.id, { segBgMediaUrl: URL.createObjectURL(f) }); }}
                                  style={{ fontSize: 11, color: '#94a3b8', flex: 1 }} />
                              </label>
                            )}
                            {item.segBgMediaUrl ? (
                              <div style={{ borderRadius: 6, overflow: 'hidden', maxHeight: 80, background: '#000', position: 'relative' }}>
                                {mediaType === 'image'
                                  ? <img src={item.segBgMediaUrl} alt="bg" style={{ width: '100%', height: 80, objectFit: 'cover' }} />
                                  : <video src={item.segBgMediaUrl} muted loop autoPlay playsInline style={{ width: '100%', height: 80, objectFit: 'cover' }} />}
                                <button onClick={() => updateSegItem(item.id, { segBgMediaUrl: undefined })}
                                  style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '2px 6px' }}>✕</button>
                              </div>
                            ) : (
                              <div style={{ fontSize: 11, color: '#475569', textAlign: 'center', padding: '8px 0' }}>
                                Aucun {mediaType === 'image' ? 'image' : 'vidéo'} sélectionné(e)
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* background-pixelate params */}
                  {item.segEffect === 'background-pixelate' && (
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Taille pixel : {item.segPixelSize}px</div>
                      <input type="range" min={4} max={40} value={item.segPixelSize} style={{ width: '100%', accentColor: '#8b5cf6' }}
                        onChange={e => updateSegItem(item.id, { segPixelSize: +e.target.value })} />
                    </div>
                  )}

                  {/* silhouette params */}
                  {item.segEffect === 'silhouette' && (
                    <>
                      <div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Couleur silhouette</div>
                        <input type="color" value={item.segSilhouetteColor}
                          onChange={e => updateSegItem(item.id, { segSilhouetteColor: e.target.value })}
                          style={{ width: 48, height: 32, padding: 2, background: '#1e293b', border: '1px solid #475569', borderRadius: 4, cursor: 'pointer' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Opacité : {Math.round(item.segSilhouetteOpacity * 100)}%</div>
                        <input type="range" min={10} max={100} value={Math.round(item.segSilhouetteOpacity * 100)} style={{ width: '100%', accentColor: '#8b5cf6' }}
                          onChange={e => updateSegItem(item.id, { segSilhouetteOpacity: +e.target.value / 100 })} />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={item.segSilhouetteOnVideo !== false}
                          onChange={e => updateSegItem(item.id, { segSilhouetteOnVideo: e.target.checked })} />
                        Sur la vidéo originale
                      </label>
                    </>
                  )}

                  {/* body-parts params */}
                  {item.segEffect === 'body-parts' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 10, color: '#6366f1', marginBottom: 2 }}>
                        Modèle : SelfieMulticlass (visage · cheveux · corps · vêtements · accessoires)
                      </div>
                      {(Object.keys(SEG_PART_LABELS) as SegPartGroup[]).map(group => {
                        const parts = item.segPartGroups ?? DEFAULT_SEG_PARTS;
                        const gcfg = parts[group];
                        return (
                          <div key={group} style={{ background: gcfg.enabled ? '#1e1a3a' : '#111827', borderRadius: 6, padding: '8px 10px', border: `1px solid ${gcfg.enabled ? '#5b21b6' : '#1e293b'}` }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: gcfg.enabled ? 8 : 0 }}>
                              <input type="checkbox" checked={gcfg.enabled}
                                onChange={e => updateSegItem(item.id, { segPartGroups: { ...(item.segPartGroups ?? DEFAULT_SEG_PARTS), [group]: { ...gcfg, enabled: e.target.checked } } })} />
                              {SEG_PART_LABELS[group]}
                            </label>
                            {gcfg.enabled && (
                              <>
                                <select value={gcfg.effect}
                                  onChange={e => updateSegItem(item.id, { segPartGroups: { ...(item.segPartGroups ?? DEFAULT_SEG_PARTS), [group]: { ...gcfg, effect: e.target.value as SegPartEffect } } })}
                                  style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', padding: '4px 6px', fontSize: 12, marginBottom: 6 }}>
                                  <option value="blur">Flou</option>
                                  <option value="color">Colorisation</option>
                                  <option value="pixelate">Pixelisation</option>
                                  <option value="hide">Masquer</option>
                                </select>
                                {gcfg.effect === 'color' && (
                                  <div style={{ marginBottom: 6 }}>
                                    <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 8 }}>Couleur</span>
                                    <input type="color" value={gcfg.color}
                                      onChange={e => updateSegItem(item.id, { segPartGroups: { ...(item.segPartGroups ?? DEFAULT_SEG_PARTS), [group]: { ...gcfg, color: e.target.value } } })}
                                      style={{ width: 36, height: 24, padding: 1, border: '1px solid #475569', borderRadius: 3, cursor: 'pointer' }} />
                                  </div>
                                )}
                                {gcfg.effect !== 'hide' && (
                                  <>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>Intensité : {gcfg.intensity}%</div>
                                    <input type="range" min={10} max={100} value={gcfg.intensity} style={{ width: '100%', accentColor: '#8b5cf6' }}
                                      onChange={e => updateSegItem(item.id, { segPartGroups: { ...(item.segPartGroups ?? DEFAULT_SEG_PARTS), [group]: { ...gcfg, intensity: +e.target.value } } })} />
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ fontSize: 10, color: '#475569', lineHeight: 1.5 }}>
                    ⚡ Modèles : <span style={{ color: '#6366f1' }}>@mediapipe/tasks-vision</span> (GPU WASM) — inférence synchrone temps réel.<br/>
                    {hasSegEffectsFooter ? '🎬 Export WebM canvas + MediaPipe' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={S.footer}>
        {isExporting ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#94a3b8' }}>
            <div style={{ width: 14, height: 14, border: '2px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'vfxSpin 0.8s linear infinite' }} />
            {exportMsg}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: '#475569' }}>
            {zones.length} zone(s) · {globalEffects.length} effet(s) global(aux)
            {hasSegEffectsFooter ? ' · 🤖 IA active → export WebM' : ''}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={onCancel} style={S.btn} disabled={isExporting}>Annuler</button>
        <button
          onClick={handleExport}
          disabled={isExporting || (globalEffects.length === 0 && zones.every(z => z.effects.length === 0) && !hasSegEffectsFooter)}
          style={{ ...S.btnPrimary, opacity: isExporting ? 0.7 : 1, cursor: isExporting ? 'wait' : 'pointer' }}
        >
          {isExporting ? '⏳ Export en cours…' : hasSegEffectsFooter ? '🤖 Exporter avec IA' : '💾 Exporter avec effets'}
        </button>
      </div>

      <style>{`@keyframes vfxSpin { to { transform: rotate(360deg) } }`}</style>
    </div>,
    document.body
  );
};

export default VideoEffectsEditor;
